import React, {
    type HTMLAttributes,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react"

interface AnimateHeightProps extends HTMLAttributes<HTMLDivElement> {
    minified?: boolean
    maxHeightInPx?: number
    children: ReactNode
    removeFromDomWhenClosed?: boolean
}

const AnimateHeight: React.FC<AnimateHeightProps> = ({
    minified = true,
    children,
    maxHeightInPx,
    removeFromDomWhenClosed = false,
    ...rest
}) => {
    const ref = useRef<HTMLDivElement | null>(null)
    const [maxHeight, setMaxHeight] = useState("0px")
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [shouldRenderChildren, setShouldRenderChildren] = useState(
        !minified || !removeFromDomWhenClosed
    )

    // Set maxHeight initially and on minified changes
    const updateHeight = useCallback(() => {
        if (ref.current) {
            const fullHeight = ref.current.scrollHeight
            const height = minified
                ? "0px"
                : `${Math.min(fullHeight, maxHeightInPx || fullHeight)}px`
            setMaxHeight(height)
            setIsTransitioning(true)
        }
    }, [minified, maxHeightInPx])

    // below is to update the height when content changes, also open/close height animation
    useEffect(() => {
        // Handle removing from DOM when closed (if removeFromDomWhenClosed is true)
        let removeTimer: NodeJS.Timeout | null = null
        if (removeFromDomWhenClosed) {
            if (minified) {
                // When closing, wait for animation to complete before removing from DOM
                removeTimer = setTimeout(() => {
                    setShouldRenderChildren(false)
                }, 200) // Match transition duration
            } else {
                // When opening, add to DOM immediately so it can animate in
                setShouldRenderChildren(true)
            }
        } else {
            setShouldRenderChildren(true)
        }

        updateHeight()
        if (!ref.current || minified) {
            return () => {
                if (removeTimer) clearTimeout(removeTimer)
            }
        }

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                updateHeight()
            })
        })

        const observeChildren = (element: HTMLElement) => {
            resizeObserver.observe(element)
            const childElements = element.querySelectorAll("*")
            childElements.forEach(child => {
                if (child instanceof Element) {
                    resizeObserver.observe(child)
                }
            })
        }

        observeChildren(ref.current)

        const mutationObserver = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node instanceof Element) {
                        observeChildren(node as HTMLElement)
                    }
                })
            })

            requestAnimationFrame(() => {
                updateHeight()
            })
        })

        mutationObserver.observe(ref.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class", "style"]
        })

        return () => {
            if (removeTimer) clearTimeout(removeTimer)
            resizeObserver.disconnect()
            mutationObserver.disconnect()
        }
    }, [minified, removeFromDomWhenClosed, updateHeight])

    // Apply transition after initial load
    useEffect(() => {
        if (!isTransitioning) {
            const timer = setTimeout(() => setIsTransitioning(true), 0)
            return () => clearTimeout(timer)
        }
        return () => {}
    }, [isTransitioning])

    return (
        <div
            ref={ref}
            style={{
                maxHeight,
                overflow: "hidden",
                transition: isTransitioning ? "max-height 0.2s ease-in-out" : "none"
            }}
            {...rest}
        >
            {shouldRenderChildren && children}
        </div>
    )
}

export default AnimateHeight
